import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { Login } from '@/components/Login';
import { getRedirectDestination } from '@/utils/authRedirect';
import {
  clearProcessingRequestRedirect,
  readProcessingRequestRedirect
} from '@/services/processingRequestAuthRedirect';

/**
 * Route handler for `/` — shows Login when unauthenticated, otherwise
 * resolves the intended destination and Navigates to it.
 *
 * Precedence (highest first):
 *   1. Router state (`location.state.from.pathname` — set by ProtectedRoute)
 *   2. `?redirect=` query param (set by AuthRedirect for email deep links)
 *   3. This tab's Processing Requests return, if this integration started OAuth.
 *   4. `oauth_redirect` in localStorage (set by Login.handleGoogleLogin before
 *      the OAuth round-trip). This last-line consumer is necessary because
 *      This remains a defensive fallback if Supabase lands the browser on the
 *      project Site URL instead of the configured callback route.
 *   5. Default `/internal`.
 */
export const HomeRoute: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const processingRedirect = readProcessingRequestRedirect();

  // Keep the integration return available through repeated renders (including
  // StrictMode). Clear it only once the authenticated Navigate has committed.
  useEffect(() => {
    if (user && !loading && processingRedirect !== null) {
      clearProcessingRequestRedirect();
    }
  }, [user, loading, processingRedirect]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="w-96 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    const intendedDestination = getRedirectDestination({
      locationState: location.state,
      searchParams: new URLSearchParams(location.search),
      checkLocalStorage: true,
      consumeProcessingRequestRedirect: false
    });
    return <Navigate to={intendedDestination} replace />;
  }

  return <Login />;
};
